import { useEffect, useRef, useState } from "react";
import { ICustomCornerType } from "../../../../responses/customPanelTypes";
import { CornerHookType } from "../../../../responses/hookSource";
import { IPanelGeneratedDrawingDto } from "../../../../responses/panelGeneratedDrawingDto";
import { IPanelGeneratedModelDto, PanelTypeGenerationStatus } from "../../../../responses/panelGeneratedModelDto";
import { CornerHook } from "../../panels/cornerHook";
import {
    CladdingCell2D,
    createCornerLeftWingCladdingCells,
    createCornerRightWingCladdingCells,
} from "./2d/claddingCell2D";
import { Corner2DSideSelector } from "./2d/corner2DSideSelector";
import { PanelEditor2D } from "./2d/panelEditor2DOld";
import { Editor2DMenu, Tab, TabsVisibility } from "./editor2DMenu";
import { GeneratedPanelDrawings } from "./generatedPanelDrawings";
import { GeneratedPanelModel } from "./generatedPanelModel";
import { GeneratedPanelModelLoadedPayload } from "../../eventBus/generatedModelsEventPayloads";
import { ActivateCornerPanelHooksDesignerEventPayload } from "../../eventBus/hooksDesignerEventPayloads";
import eventBus, { IApplicationEvent } from "../../eventBus/eventDispatcher";
import "./editor2DContent.css";

type Corner2D = {
    id: string;
    panelTypeId: string;
    leftWingLength: number;
    rightWingLength: number;
    height: number;
    leftWingCladdingCells: CladdingCell2D[];
    rightWingCladdingCells: CladdingCell2D[];
    leftWingHooks: CornerHook[];
    rightWingHooks: CornerHook[];
    selectedHook: CornerHook | null;
};

type Props = {
    isMetric: boolean;
};

export const Corner2DEditorDockingPanelContent = ({ isMetric }: Props) => {
    const [selectedCornerSide, setSelectedCornerSide] = useState<Exclude<CornerHookType, CornerHookType.None>>(
        CornerHookType.CornerLeftWing,
    );
    const [corner2D, setCorner2D] = useState<Corner2D>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [model, setModel] = useState<Omit<IPanelGeneratedModelDto, "typeId">>();
    const [drawings, setDrawings] = useState<IPanelGeneratedDrawingDto[]>([]);
    const [selectedDrawingIndex, setSelectedDrawingIndex] = useState(0);
    const [is2DHookPlacementActive, setIs2DHookPlacementActive] = useState(false);
    const [selectedTab, setSelectedTab] = useState<Tab>("editor");
    const [customModelBubbleUrl, setCustomModelBubbleUrl] = useState<string | null>();
    const cornerId = useRef<string>();
    const cornerTypeId = useRef<string>();
    const customPanelType = useRef<ICustomCornerType>();

    useEffect(() => {
        const onPanelSelected = (event: IApplicationEvent<ActivateCornerPanelHooksDesignerEventPayload>) => {
            const { panel, side, leftWingHooks, rightWingHooks, selectedHook } = event.payload;

            if (side !== CornerHookType.None) {
                setSelectedCornerSide(side);
            }

            if (cornerId.current !== panel.id) {
                setIs2DHookPlacementActive(false);
                setIsLoading(true);
                setSelectedDrawingIndex(0);
            }

            const selectedCustomPanelType = event.payload.customPanelType;

            if (selectedCustomPanelType) {
                setSelectedTab("model");
            } else if (cornerTypeId.current !== panel.panelTypeId) {
                setSelectedTab("editor");
            }

            cornerId.current = panel.id;
            cornerTypeId.current = panel.panelTypeId;
            customPanelType.current = selectedCustomPanelType;

            setCustomModelBubbleUrl(selectedCustomPanelType?.bubbleUrl);

            setCorner2D({
                id: panel.id,
                panelTypeId: panel.panelTypeId,
                leftWingLength: panel.isReversed ? panel.rightWing : panel.leftWing,
                rightWingLength: panel.isReversed ? panel.leftWing : panel.rightWing,
                height: panel.height,
                leftWingCladdingCells: createCornerLeftWingCladdingCells(panel),
                rightWingCladdingCells: createCornerRightWingCladdingCells(panel),
                leftWingHooks,
                rightWingHooks,
                selectedHook: selectedHook,
            });
        };

        const onLoaded = (event: IApplicationEvent<GeneratedPanelModelLoadedPayload>) => {
            if (cornerTypeId.current !== event.payload.panelTypeId) {
                return;
            }

            setIsLoading(false);
            setModel(event.payload.model);
            setDrawings(event.payload.drawings);
        };

        eventBus.addEventListener("Dextall.Hooks.Designer.Corner.SelectionChanged", onPanelSelected);
        eventBus.addEventListener("Dextall.TypesManager.UI.CornerType.SetGeneratedModel", onLoaded);

        return () => {
            eventBus.removeEventListener("Dextall.Hooks.Designer.Corner.SelectionChanged", onPanelSelected);
            eventBus.removeEventListener("Dextall.TypesManager.UI.CornerType.SetGeneratedModel", onLoaded);
        };
    }, []);

    if (!corner2D) {
        return null;
    }

    const onHookPlacementRequested = (position: THREE.Vector2) => {
        eventBus.dispatchEvent({
            type: "Dextall.Hooks.CreateCornerPanelHookRequested",
            payload: {
                panelId: corner2D.id,
                position,
                onLeftWing: selectedCornerSide === CornerHookType.CornerLeftWing,
            },
        });
    };

    const tabsVisibility: TabsVisibility = {
        model: true,
        editor: !customModelBubbleUrl,
        drawings: !customModelBubbleUrl,
    };

    return (
        <div className="editor-2d-container" style={{ height: "calc(100% - 50px)" }}>
            <Editor2DMenu selectedTab={selectedTab} setSelectedTab={setSelectedTab} tabVisibility={tabsVisibility} />
            {selectedTab === "editor" && (
                <>
                    <Corner2DSideSelector
                        selectedCornerSide={selectedCornerSide}
                        onToggleCornerSide={(side) => setSelectedCornerSide(side)}
                    />
                    <div className="editor-2d-content">
                        <PanelEditor2D
                            length={
                                selectedCornerSide === CornerHookType.CornerLeftWing
                                    ? corner2D.leftWingLength
                                    : corner2D.rightWingLength
                            }
                            height={corner2D.height}
                            cells={
                                selectedCornerSide === CornerHookType.CornerLeftWing
                                    ? corner2D.leftWingCladdingCells
                                    : corner2D.rightWingCladdingCells
                            }
                            hooks={
                                selectedCornerSide === CornerHookType.CornerLeftWing
                                    ? corner2D.leftWingHooks
                                    : corner2D.rightWingHooks
                            }
                            selectedHook={
                                corner2D.selectedHook?.cornerHookType === selectedCornerSide
                                    ? corner2D.selectedHook
                                    : null
                            }
                            isMetric={isMetric}
                            is2DHookPlacementActive={is2DHookPlacementActive}
                            onHookPlacementRequested={onHookPlacementRequested}
                            setIs2DHookPlacementActive={setIs2DHookPlacementActive}
                        />
                    </div>
                </>
            )}
            {selectedTab === "model" && !customModelBubbleUrl && (
                <GeneratedPanelModel
                    model={model}
                    isLoading={isLoading}
                    contentType="corner"
                    panelTypeId={corner2D.panelTypeId}
                />
            )}
            {selectedTab === "model" && customModelBubbleUrl && (
                <GeneratedPanelModel
                    model={{
                        bubble: customModelBubbleUrl,
                        id: customPanelType.current!.id,
                        status: PanelTypeGenerationStatus.ModelGeneratedSuccessfully,
                    }}
                    contentType="custom-corner"
                    isLoading={false}
                    panelTypeId={customPanelType.current!.id}
                />
            )}
            {selectedTab === "drawing" && (
                <GeneratedPanelDrawings
                    model={model}
                    drawings={drawings}
                    isLoading={isLoading}
                    selectedDrawingIndex={selectedDrawingIndex}
                    setSelectedDrawingIndex={setSelectedDrawingIndex}
                    panelTypeId={corner2D.panelTypeId}
                    isPanelModel={false}
                />
            )}
        </div>
    );
};
